'use client'

import {
  FC,
  useState,
  useMemo,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
} from 'react'
import styles from './styles.module.scss'
import cx from 'classnames'

import { JoinMailingDecisionStep } from '../JoinMailingDecision/joinMailingDecisionStep'
import { MarketingConsentStep } from '../MarketingConsent/marketingConsentStep'
import { UserDetailsStep } from '../UserDetails/userDetailsStep'
import { BusinessType } from '../UserDetails'
import { CheckYourEmailStep } from '../CheckYourEmail/CheckYourEmailStep'
import { QRCodeForm } from '@/components/OpenAccount/flows/desktop/QRCodeForm'
import { PanelItem } from '@/components/Panel/PanelItem'
import { useOnboardingSteps } from '@/context/OnboardingStepContext/OnboardingStepContext'
import { RichTextContent } from '@/hooks/useRichText'
import { EmailFormValues } from '@/components/EmailForm'

export interface OpenAccountCaptureLeadCardPage {
  title: string
  mainContent: RichTextContent
  formData?: OpenAccountCapture
  onSubmit?: (values: EmailFormValues) => void
}

interface LeadLayoutProps {
  title: string
  children: ReactNode
  submissionError?: Error
  isSubmitting?: boolean
  showSuccess?: boolean
  onBack?: () => void
}

export const JoinLayout: FC<LeadLayoutProps> = ({ title, children }) => (
  <PanelItem className={styles.baseContent}>
    <div className={styles.content}>
      <div className={cx(styles.container, styles.warmStone)}>
        <div className={styles.titleContainer}>
          <h2>{title}</h2>
        </div>
        <div className={styles.childrenContainer}>{children}</div>
      </div>
    </div>
  </PanelItem>
)

export interface StepCommonProps {
  formData: OpenAccountCapture | null
  setFormData: Dispatch<SetStateAction<OpenAccountCapture | null>>
}

export type OpenAccountCapture = {
  email: string
  firstName: string
  businessType: BusinessType | undefined
  policyAgreed: boolean
  isJoiningMailingList: boolean | undefined
}

export function withOpenAccountCaptureDefaults(
  data: Partial<OpenAccountCapture> | null | undefined,
): OpenAccountCapture {
  return {
    email: data?.email ?? '',
    firstName: data?.firstName ?? '',
    businessType: data?.businessType,
    policyAgreed: data?.policyAgreed ?? false,
    isJoiningMailingList: data?.isJoiningMailingList,
  }
}

const QRCodeStep: FC<StepCommonProps> = () => <QRCodeForm />
const MailingListQRCodeStep: FC<
  StepCommonProps & { isHashOpenAccount: boolean }
> = ({ isHashOpenAccount }) => (
  <QRCodeForm
    title="We've sent you a confirmation email"
    instructions="Click the link and follow the steps below to start your application"
    isHashOpenAccount={isHashOpenAccount}
  />
)

export const OpenAccountPage = () => {
  const { step, goToStep } = useOnboardingSteps()
  const isHashOpenAccount =
    typeof window !== 'undefined' && window.location.hash === '#open-account'
  const [formData, setFormData] = useState<OpenAccountCapture | null>(() =>
    isHashOpenAccount
      ? withOpenAccountCaptureDefaults({ isJoiningMailingList: true })
      : null,
  )

  type StepComponent = FC<StepCommonProps>

  const joiningMailingStepsList: StepComponent[] = useMemo(
    () => [
      MarketingConsentStep as StepComponent,
      UserDetailsStep as StepComponent,
      CheckYourEmailStep as StepComponent,
      MailingListQRCodeStep as StepComponent,
    ],
    [],
  )

  const joiningStepsList: StepComponent[] = useMemo(() => [QRCodeStep], [])

  useEffect(() => {
    if (isHashOpenAccount && formData?.isJoiningMailingList === true) {
      const target = joiningMailingStepsList.length // final step index
      if (step !== target) {
        goToStep(target)
      }
    }
  }, [
    isHashOpenAccount,
    formData?.isJoiningMailingList,
    step,
    goToStep,
    joiningMailingStepsList.length,
  ])

  const Active: StepComponent = (() => {
    if (formData?.isJoiningMailingList === true) {
      if (step === 0) return JoinMailingDecisionStep as StepComponent
      return joiningMailingStepsList[step - 1] || QRCodeStep
    }
    if (formData?.isJoiningMailingList === false) {
      if (step === 0) return JoinMailingDecisionStep as StepComponent
      return joiningStepsList[step - 1] || QRCodeStep
    }
    return JoinMailingDecisionStep as StepComponent // undecided state
  })()

  return <Active formData={formData} setFormData={setFormData} />
}
